import React, { useEffect, useState, useContext, useMemo } from 'react'
import { Layout, Menu, Avatar, Select } from 'antd'
import { ShopOutlined } from '@ant-design/icons'
import { useNavigate, useLocation } from 'react-router-dom'
import { UserContext } from '../../../context/userContext'
import { StoreContext } from '../../../context/storeContext'
import {
  getUserDisplayInfo,
  getNavigationMenuItems
} from '../../../utils/appUtils'
import styles from './sideNavigationMenu.module.scss'

const { Sider } = Layout
const { Option } = Select

const Sidebar = ({ collapsed, topOffset = 0 }) => {
  const navigate = useNavigate()
  const location = useLocation()
  const [isMobile, setIsMobile] = useState(false)
  const [storeSearchText, setStoreSearchText] = useState('')

  const {
    graphData,
    profileImageData,
    cognitoData,
    userRole,
    userAccessData
  } = useContext(UserContext) || {}

  // Get store information from store context 
  const storeContext = useContext(StoreContext)
  const {
    name: currentStoreName,
    id: currentStoreId, 
    setStore,
    getAvailableStores,
    hasMultipleStores,
    getUserStores
  } = storeContext || {}

  const { displayName, userInitial, loginType } = getUserDisplayInfo(cognitoData, graphData)
  const formattedDisplayName = loginType === 'cognito' ? displayName.toUpperCase() : displayName
  const hasProfilePhoto = loginType === 'microsoft' && profileImageData?.photo
  const isCognitoUser = loginType === 'cognito'

  // Function to check if user has access to a specific app/screen
  const hasAccessToApp = (appId) => {
    if (!userAccessData || !userAccessData.message) {
      // If no user access data available, allow access (fallback behavior)
      return true
    }

    const { permissions, accessibleScreens } = userAccessData.message

    // Check if app is in accessibleScreens array
    if (accessibleScreens && accessibleScreens.includes(appId)) {
      return true
    }

    // Also check permissions object for access property
    if (permissions && permissions[appId] && permissions[appId].access === true) {
      return true
    }

    return false
  }

  // Function to filter navigation menu items based on user access
  const getFilteredNavigationMenuItems = () => {
    // Pass current store ID to getNavigationMenuItems for store-based filtering
    const allMenuItems = getNavigationMenuItems(navigate, getClassName, currentStoreId)

    if (!userAccessData || !userAccessData.message) {
      // If no user access data, show all menu items (fallback behavior)
      // This ensures navigation is visible while access data is loading
      return allMenuItems
    }

    // Check if user has store access - if stores array is empty, don't show menu items
    const { stores } = userAccessData.message
    if (stores && Array.isArray(stores) && stores.length === 0) {
      // Return empty array to hide all menu items when user has no store access
      return []
    }

    // Filter menu items based on user access
    return allMenuItems.filter(menuItem => {
      // Always show Home
      if (menuItem.key === '/home') {
        return true
      }

      // Map menu item keys to permission/app IDs
      const menuToAppMap = {
        '/petty-cash': 'pettyCash',
        '/rfp': 'rfp',
        '/vendor': 'vendor',
        '/shelf-edge-labels': 'shelfEdgeLabels',
        '/clearance-labels': 'clearanceLabels',
        '/saved-labels': 'savedLabels',
        '/product-lookup': 'productLookup',
        '/chatbot': 'aiChatbot'
      }

      // Apps that don't require permission checks - always accessible
      const alwaysAccessiblePaths = [
        '/shelf-edge-labels',
        '/clearance-labels',
        '/saved-labels',
        '/product-lookup',
        '/chatbot'
      ]

      if (alwaysAccessiblePaths.includes(menuItem.key)) {
        return true
      }

      const appId = menuToAppMap[menuItem.key]
      if (appId) {
        return hasAccessToApp(appId)
      }

      // For any unmapped menu items, show them by default (change from hiding)
      // This ensures custom menu items are visible unless specifically restricted
      return true
    })
  }

  // Get store options for dropdown based on user access API response
  const storeOptions = useMemo(() => {
    if (!hasMultipleStores) {
      return []
    }

    // Use the helper function from StoreContext
    const options = getAvailableStores ? getAvailableStores() : []

    return options
  }, [hasMultipleStores, getAvailableStores])

  // Filter stores based on search text
  const filteredStoreOptions = useMemo(() => {
    if (!storeSearchText) return storeOptions
    return storeOptions.filter(store =>
      store.name.toLowerCase().includes(storeSearchText.toLowerCase())
    )
  }, [storeOptions, storeSearchText])

  // Determine if user should see store selector - updated logic
  const shouldShowStoreSelector = useMemo(() => {
    // Show store selector if user has multiple stores (regardless of login type)
    return hasMultipleStores && storeOptions.length > 1
  }, [hasMultipleStores, storeOptions])

  // Set default store if not already set and user has stores
  useEffect(() => {
    if (storeOptions.length > 0 && !currentStoreName && setStore) {
      // Set the first store as default
      const defaultStore = storeOptions[0]
      setStore({
        name: defaultStore.name,
        id: defaultStore.id
      }, false) // Don't trigger refresh on initial set
    }
  }, [storeOptions, currentStoreName, setStore])

  // Format role display text
  const getRoleDisplayText = () => {
    if (isCognitoUser) return 'Store Assistant'
    if (!userRole) return 'No role assigned'
    return userRole
  }

  // Handle store change in dropdown
  const handleStoreChange = (value) => {
    const selectedStoreObj = storeOptions.find(store => store.name === value)

    if (setStore && selectedStoreObj) {
      setStore({
        name: selectedStoreObj.name,
        id: selectedStoreObj.id
      }, true) // Trigger refresh
    }
  }

  const getClassName = (path) => {
    return location.pathname === path ? styles.customSelectedItem : ''
  }

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768)
    }

    checkMobile()
    window.addEventListener('resize', checkMobile)
    return () => window.removeEventListener('resize', checkMobile)
  }, [])

  // Get filtered menu items based on user access and current store
  const filteredMenuItems = getFilteredNavigationMenuItems()

  return (
    <Sider
      trigger={null}
      collapsible
      collapsed={collapsed}
      width={250}
      collapsedWidth={isMobile ? 0 : 80}
      className={styles.sidebar}
      style={{
        height: `calc(100vh - ${topOffset}px)`,
        top: `${topOffset}px`,
        left: isMobile && collapsed ? '-100%' : '0'
      }}
    >
      {/* User Panel - only visible when sidebar is expanded */}
      {!collapsed && (
        <div className={styles.userPanelContainer}>
          <div className={styles.userAvatar}>
            {hasProfilePhoto ? (
              <Avatar
                src={profileImageData.photo}
                size={86}
                style={{ border: '0' }}
              />
            ) : (
              <Avatar
                size={86}
                style={{
                  backgroundColor: 'white',
                  color: 'black',
                  fontSize: '36px',
                  border: '0'
                }}
              >
                {userInitial}
              </Avatar>
            )}
          </div>
          <div className={styles.welcomeText}>Welcome</div>
          <div className={styles.userName}>{formattedDisplayName}</div>
          <div className={styles.userRole}>{getRoleDisplayText()}</div>

          {/* Static Store Name - for users with single store only */}
          {(!shouldShowStoreSelector && currentStoreName) && (
            <div className={styles.storeName}>
              {currentStoreName}
            </div>
          )}

          {/* Store Dropdown - for users with multiple stores (both Cognito and AD) */}
          {shouldShowStoreSelector && storeOptions.length > 0 && (
            <div className={styles.storeSelector}>
              <Select
                value={currentStoreName}
                onChange={(value) => {
                  setStoreSearchText('')
                  handleStoreChange(value)
                }}
                style={{ width: '90%' }}
                suffixIcon={<ShopOutlined />}
                dropdownStyle={{ padding: '0' }}
                popupMatchSelectWidth={false}
                placeholder="Select Store"
                popupClassName={styles.storeDropdown}
                labelInValue={false}
                optionLabelProp="label"
                showSearch={false}
                onDropdownVisibleChange={(open) => {
                  if (!open) {
                    setStoreSearchText('')
                  }
                }}
                dropdownRender={(menu) => (
                  <div>
                    <div style={{ padding: '8px 8px' }}>
                      <input
                        type="text"
                        placeholder="Search stores..."
                        value={storeSearchText}
                        onChange={(e) => setStoreSearchText(e.target.value)}
                        onKeyDown={(e) => {
                          e.stopPropagation()
                        }}
                        onClick={(e) => {
                          e.stopPropagation()
                        }}
                        autoFocus
                        style={{
                          width: '100%',
                          padding: '6px 8px',
                          border: '1px solid #d9d9d9',
                          borderRadius: '2px',
                          fontSize: '14px',
                          boxSizing: 'border-box',
                          color: '#000',
                          backgroundColor: '#fff'
                        }}
                      />
                    </div>
                    <div style={{ borderTop: '1px solid #f0f0f0' }}>
                      {menu}
                    </div>
                  </div>
                )}
              >
                {(storeSearchText ? filteredStoreOptions : storeOptions).map(store => (
                  <Option
                    key={store.id}
                    value={store.name}
                    label={`Store: ${store.name}`}
                  >
                    {store.name}
                  </Option>
                ))}
              </Select>
            </div>
          )}

          {/* Show message if user has no store access */}
          {(!getUserStores || getUserStores().length === 0) && (
            <div className={styles.storeName} style={{ color: '#ff4d4f', fontSize: '12px' }}>
              No store access
            </div>
          )}
        </div>
      )}

      <Menu
        theme="dark"
        mode="inline"
        selectedKeys={[location.pathname]}
        defaultSelectedKeys={['/home']}
        className={styles.sidebarMenu}
        items={filteredMenuItems}
      />
    </Sider>
  )
}

export default Sidebar